/**
 * The side panel's working copy of a nudge, and the payload it saves.
 *
 * Length and count limits are enforced by the inputs themselves (maxLength, the hidden "Add
 * question"), so they are not re-checked here; the server stays the authority and its 400 message
 * is shown as is. Text is trimmed, not run through `sanitizeString`: that is DOMPurify, which
 * entity-escapes "&" into "&amp;", and Kim writes from the prompt word for word.
 */

import { createRuleDraft, draftsToRules, ruleToDraft, type NudgeFieldMap, type NudgeRuleDraft } from './nudge-rules';
import type { CreateNudgeDto, Nudge } from './types';
import type { NudgePanelState } from './components/nudge-panel';

export const NUDGE_NAME_MAX_LENGTH = 80;
export const NUDGE_PROMPT_MAX_LENGTH = 1000;
export const NUDGE_SUGGESTION_MAX_LENGTH = 200;
export const NUDGE_MAX_SUGGESTIONS = 3;

/** One question box. */
export interface NudgeSuggestionDraft {
	/** Local identity for React keys, so removing a box never hands its DOM node to the next. Never sent. */
	id: string;
	text: string;
}

export interface NudgeDraft {
	name: string;
	prompt: string;
	rules: NudgeRuleDraft[];
	/** Question 1..3. Always at least one box on screen, even when nothing is saved. */
	suggestions: NudgeSuggestionDraft[];
}

/** The payload both create and update send: the panel always saves the whole nudge. */
export type NudgeSavePayload = Required<Pick<CreateNudgeDto, 'name' | 'prompt' | 'suggestions' | 'rules'>>;

/** Which card shows the error: the conditions card, or the "What to nudge" card for the rest. */
export type NudgePayloadErrorSection = 'rules' | 'other';

export type NudgePayloadResult =
	| { ok: true; value: NudgeSavePayload }
	| { ok: false; error: string; section: NudgePayloadErrorSection };

let suggestionSequence = 0;

export const createSuggestionDraft = (text = ''): NudgeSuggestionDraft => {
	suggestionSequence += 1;
	return { id: `nudge-suggestion-${suggestionSequence}`, text };
};

/** A new nudge: the default name, one empty row and one empty question, as the design opens it. */
export const newNudgeDraft = (name: string): NudgeDraft => ({
	name,
	prompt: '',
	rules: [createRuleDraft()],
	suggestions: [createSuggestionDraft()]
});

export const nudgeToDraft = (nudge: Nudge): NudgeDraft => ({
	name: nudge.name,
	prompt: nudge.prompt,
	rules: nudge.rules.length > 0 ? nudge.rules.map(ruleToDraft) : [createRuleDraft()],
	suggestions: (nudge.suggestions.length > 0 ? nudge.suggestions : ['']).map((text) => createSuggestionDraft(text))
});

/** "Nudge N" for the next nudge, skipping any number already taken so two never share a name. */
export const nextNudgeName = (existingNames: string[]): string => {
	const taken = new Set(existingNames.map((name) => name.trim().toLowerCase()));
	let number = existingNames.length + 1;
	while (taken.has(`nudge ${number}`)) number += 1;
	return `Nudge ${number}`;
};

/** The side panel opened on a new nudge, named after the nudges that already exist. */
export const newNudgePanelState = (nudges: Pick<Nudge, 'name'>[]): NudgePanelState => ({
	mode: 'edit',
	nudge: null,
	defaultName: nextNudgeName(nudges.map((nudge) => nudge.name))
});

export const buildNudgePayload = (draft: NudgeDraft, fieldMap: NudgeFieldMap): NudgePayloadResult => {
	const rules = draftsToRules(draft.rules, fieldMap);
	if (!rules.ok) return { ...rules, section: 'rules' };

	const name = draft.name.trim();
	if (!name) return { ok: false, error: 'Give the nudge a name.', section: 'other' };

	// Empty question boxes are dropped, not refused: the panel always shows at least one.
	const suggestions = draft.suggestions.map((suggestion) => suggestion.text.trim()).filter(Boolean);

	return { ok: true, value: { name, prompt: draft.prompt.trim(), suggestions, rules: rules.value } };
};
